import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { CheckCircle, ArrowLeft } from "lucide-react";

const LawnCareWebsiteChecklist = () => {
  return (
    <>
      <SEO 
        title="Lawn Care Website Checklist: Essential Features You Need"
        description="Complete checklist of must-have features for lawn care websites. Learn what pages, design elements, and functionality help convert visitors into customers."
        keywords="lawn care website checklist, lawn care website features, websites for lawn care, lawn care web design"
        canonical="https://www.leafrushmarketing.com/resources/lawn-care-website-checklist/"
        ogUrl="https://www.leafrushmarketing.com/resources/lawn-care-website-checklist/"
      />
      <article className="flex flex-col">
        {/* Back Link */}
        <section className="py-6 border-b">
          <div className="container px-4 md:px-6">
            <NavLink to="/resources" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Resources
            </NavLink>
          </div>
        </section>

        {/* Article Header */}
        <section className="py-12 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                Lawn Care Website Checklist: Essential Features You Need
              </h1>
              <p className="text-xl text-muted-foreground">
                Your lawn care website should make it easy for homeowners to understand your services, 
                trust your business, and reach out for a quote. Here's exactly what your site needs to convert visitors into customers.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-12 md:pb-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto prose prose-lg">
              <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-4">The Core Pages Every Lawn Care Website Needs</h2>
              
              <div className="space-y-4 not-prose">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Homepage</h3>
                    <p className="text-muted-foreground">Clear headline, service overview, trust signals (years in business, reviews), and a strong call-to-action.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Services Pages</h3>
                    <p className="text-muted-foreground">Individual pages for each service (lawn mowing, fertilization, aeration, etc.) with clear descriptions and pricing guidance.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">About Page</h3>
                    <p className="text-muted-foreground">Your story, credentials, service area, and why customers should choose you over competitors.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Gallery / Portfolio</h3>
                    <p className="text-muted-foreground">Before/after photos and completed project showcases that prove the quality of your work.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Reviews / Testimonials</h3>
                    <p className="text-muted-foreground">Real customer feedback builds trust and credibility fast.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Contact Page</h3>
                    <p className="text-muted-foreground">Phone number, email, service area map, and an easy quote request form.</p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Design & User Experience Essentials</h2>
              
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Mobile-first design:</strong> Most customers will visit your site from their phone.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Fast load times:</strong> Pages should load in under 3 seconds or visitors leave.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Clear navigation:</strong> Visitors should find what they need in 2 clicks or less.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Click-to-call buttons:</strong> Make it easy to reach you directly from any page.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span><strong>Professional imagery:</strong> High-quality photos of your work, not generic stock images.</span>
                </li>
              </ul>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Local SEO Must-Haves</h2>
              
              <p className="text-muted-foreground mb-4">
                Your website needs to be optimized for local searches so customers in your area can find you:
              </p>

              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Service area clearly mentioned on multiple pages</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Google Business Profile linked and optimized</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>City/neighborhood names used naturally in page content</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>NAP (Name, Address, Phone) consistent across all pages</span>
                </li>
              </ul>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Ready to Build a High-Converting Lawn Care Website?</h2>
              
              <p className="text-muted-foreground mb-6">
                At Leaf Rush Marketing, we build websites for lawn care and landscaping businesses that check every box on this list—and more. 
                Our sites are designed specifically for your industry, optimized for local search, and built to turn visitors into paying customers.
              </p>

              <p className="text-muted-foreground mb-8">
                Best part? We build your site first at $0 upfront. You only start your subscription once you love it and it goes live.
              </p>

              <div className="not-prose">
                <NavLink to="/contact#get-started">
                  <Button size="lg" className="text-lg px-8">
                    Get Started
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default LawnCareWebsiteChecklist;
